import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { ExportAttachmentStatus } from "models/propTypes/revisorPropTypes";
import { WintSuperkollAPIModel, WintSuperkollAPIModelExportItems } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { DateRange } from "models/formTypes/util";
//#endregion
//#region components
import { useAnnualAccountService, useDocumentService } from "serviceHooks";
import { useLastReconciliation } from "components/accounting";
//#endregion
import { ExportItemsService } from "services";
import { LocalizationHelperInstance } from "utils/helpers";
import { getExportFailureReason } from "./exportFailure";

const useExportAttachments = () => {
  const { dowloadTaxCalculationReport, downloadPdf } = useAnnualAccountService();

  const { queueExportSIEFile, exportSharedFolderForFinancialYear } = useDocumentService();
  const annualAccountsFinancialYear = useSelector(
    (store: AppState) => store.auth.selectedCompany.annualAccountsFinancialYear
  );

  const { getLastReconciliation, lastReconciliation } = useLastReconciliation();
  const [reconciliationError, setReconciliationError] = React.useState<unknown>();

  React.useEffect(() => {
    getLastReconciliation().catch(setReconciliationError);
  }, []);

  const noPermissionText = LocalizationHelperInstance.getFormattedMessage(
    TranslationKeys.Revisor.ExportFailedNoPermission
  );
  const failed = (typeKey: string) => (error: unknown) =>
    ExportAttachmentStatus.createError(
      LocalizationHelperInstance.getFormattedMessage(typeKey),
      getExportFailureReason(error, noPermissionText),
      typeKey
    );

  const exportReconciliationsReport = async () => {
    const typeText = LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.Revisor.FinancialStatementAttachments
    );

    const reconciliationId = lastReconciliation?.reconciliationId;

    if (!reconciliationId) {
      return reconciliationError || lastReconciliation === undefined
        ? failed(TranslationKeys.Revisor.FinancialStatementAttachments)(reconciliationError)
        : ExportAttachmentStatus.createError(
            typeText,
            LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Revisor.ExportFailedNoReconciliation),
            TranslationKeys.Revisor.FinancialStatementAttachments
          );
    }

    return ExportItemsService.queueExport(
      reconciliationId,
      WintSuperkollAPIModelExportItems.ExportItemType.FullAuditSheetPdfWithAttachments,
      false
    )
      .then(() => ExportAttachmentStatus.createSuccess(typeText))
      .catch(failed(TranslationKeys.Revisor.FinancialStatementAttachments));
  };

  const exportSIEFiles = async (previousPeriod: DateRange, currentPeriod?: DateRange) => {
    const typeText = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Revisor.SIEFiles);

    let results: Promise<any>;

    const previousPeriodSIEFile = queueExportSIEFile({
      startDate: previousPeriod.start!,
      endDate: previousPeriod.end!,
    });

    if (currentPeriod) {
      const currentPeriodSIEFile = queueExportSIEFile({
        startDate: currentPeriod.start!,
        endDate: currentPeriod.end!,
      });
      results = Promise.all([previousPeriodSIEFile, currentPeriodSIEFile]);
    } else {
      results = previousPeriodSIEFile;
    }

    return results
      .then(() => ExportAttachmentStatus.createSuccess(typeText))
      .catch(failed(TranslationKeys.Revisor.SIEFiles));
  };

  const exportAuditFolder = async () => {
    const typeText = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Revisor.OtherDocuments);

    return exportSharedFolderForFinancialYear(annualAccountsFinancialYear!.id)
      .then(() => ExportAttachmentStatus.createSuccess(typeText))
      .catch(failed(TranslationKeys.Revisor.OtherDocuments));
  };

  const exportTaxCalculationReport = async () => {
    const typeText = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Revisor.TaxCalculationReport);

    return dowloadTaxCalculationReport(annualAccountsFinancialYear!.id)
      .then(() => ExportAttachmentStatus.createSuccess(typeText))
      .catch(failed(TranslationKeys.Revisor.TaxCalculationReport));
  };

  const exportAnnualReport = async () => {
    const typeText = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.AnnualReport);

    return downloadPdf(annualAccountsFinancialYear!.id, WintSuperkollAPIModel.FinancialStatementType.AnnualReport)
      .then(() => ExportAttachmentStatus.createSuccess(typeText))
      .catch(failed(TranslationKeys.AnnualAccount.AnnualReport));
  };

  const exportAttachments = async (
    isAutoAnnualAccount: boolean,
    previousPeriod: DateRange,
    currentPeriod?: DateRange,
    includeOtherDocuments = true
  ): Promise<ExportAttachmentStatus[]> => {
    const exports = [exportReconciliationsReport()];

    isAutoAnnualAccount && exports.push(exportSIEFiles(previousPeriod, currentPeriod));
    includeOtherDocuments && exports.push(exportAuditFolder());
    isAutoAnnualAccount && exports.push(exportTaxCalculationReport());
    isAutoAnnualAccount && exports.push(exportAnnualReport());

    return Promise.all(exports);
  };

  return { exportAttachments };
};

export default useExportAttachments;
